import {Breadcrumb as PepsicoDsBreadcrumb} from '@pepsico-ds/ui'
import PropTypes from 'prop-types'
import {useNavigate} from 'react-router-dom'

/**
 * Breadcrumb component that renders a breadcrumb navigation using the PepsicoDsBreadcrumb component.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.uiSchema - The UI schema containing breadcrumb data and styles.
 * @param {Array} props.uiSchema.breadcrumbs - The array of breadcrumb items to be displayed.
 * @param {boolean} [props.uiSchema.isDark=false] - Flag to determine if the breadcrumb should be rendered in dark mode.
 * @param {string} [props.uiSchema.className] - Additional CSS class names to apply to the breadcrumb.
 * @param {string} props.id - The unique identifier for the breadcrumb component.
 * @returns {JSX.Element} The rendered breadcrumb component.
 */
const Breadcrumb = ({uiSchema, id}) => {
  const {breadcrumbs, isDark = false, className} = uiSchema
  const navigate = useNavigate()

  // PepsicoDsBreadcrumb renders each item as a native <a href> and calls the
  // consumer's onClick with no event, so preventDefault isn't an option. A
  // real anchor click is a full page navigation, which 404s for any id that
  // contains a dot (e.g. "B2B.US.123") because the server treats it as a
  // static file request instead of falling back to the SPA shell. Drop href
  // and drive navigation entirely through react-router's navigate instead.
  const navigableBreadcrumbs = breadcrumbs.map(({href, onClick, ...item}) => {
    if (onClick || !href) return {...item, href, onClick}
    return {...item, onClick: () => navigate(href)}
  })

  return (
    <span 
      id={`breadcrumb` + (id ? `_${id}` : '')} 
      data-testid={`breadcrumb` + (id ? `_${id}` : '')}
    >
      <PepsicoDsBreadcrumb breadcrumbs={navigableBreadcrumbs} isDark={isDark} className={className} />
    </span>
  )
}

Breadcrumb.propTypes = {
  uiSchema: PropTypes.shape({
    breadcrumbs: PropTypes.array.isRequired,
    isDark: PropTypes.bool,
    className: PropTypes.string,
  }).isRequired,
  id: PropTypes.string.isRequired,
}

export default Breadcrumb
